import { ShoppingBag } from 'lucide-react';
import { useRef } from 'react';
import CardTitle from '@/domains/mobile-web/components/CardTitle/CardTitle';
import {
  AVAILABILITY_STATUS,
  type list_availability_status,
} from '@/domains/mobile-web/constant/listBook.constant';
import type { ProductSummary } from '@/domains/mobile-web/types/product.types';
import { categoryLabel } from '@/domains/mobile-web/utils/bundle';
import { formatPrice, priceView } from '@/domains/mobile-web/utils/price';
import Button from '@/shared/components/ui/Button';
import { useColorStore } from '@/store/colorStore';

interface CardProductBundlingProps {
  product: ProductSummary;
  onBuy?: (product: ProductSummary) => void;
  /** `origin` is the cover (or the button) the fly-to-cart thumbnail takes off from */
  onAddCart?: (product: ProductSummary, origin: HTMLElement) => void;
  /** Horizontal layout: click anywhere on the card except its buttons */
  onOpen?: (product: ProductSummary) => void;
  type?: 'vertical' | 'horizontal';
  onCollect?: () => void;
  onPacket?: () => void;
  packet?: list_availability_status;
  /** Image shown instead of the list thumbnail (Home: the full-size cover) */
  imageUrl?: string | null;
}

const CardProductBundling = ({
  product,
  packet,
  onBuy,
  type = 'horizontal',
  onAddCart,
  onOpen,
  onCollect,
  onPacket,
  imageUrl,
}: CardProductBundlingProps) => {
  const { NEWCOLOR } = useColorStore();
  const image = imageUrl ?? product.image_url;
  const statusPacket = packet ? AVAILABILITY_STATUS[packet] : undefined;
  const { hasDiscount, original, final } = priceView(product);
  const coverRef = useRef<HTMLImageElement>(null);

  const statusBadge = statusPacket && (
    <p
      className="w-fit rounded-[6px] px-2 py-1 reg12 whitespace-nowrap"
      style={{ backgroundColor: NEWCOLOR[statusPacket.bg], color: statusPacket.color }}
    >
      {statusPacket.label}
    </p>
  );

  return (
    <>
      {type === 'horizontal' ? (
        <article className="relative w-full shadowCard p-3 flex flex-col gap-3">
          <figure className="aspect-[16/9] w-full overflow-hidden rounded-[12px]">
            {image && (
              <img
                ref={coverRef}
                src={image}
                alt={product.title}
                className="block h-full w-full object-cover"
              />
            )}
          </figure>

          <div className="flex flex-col gap-1">
            {statusBadge}
            <h3 className="line-clamp-2  title3bold lg:text-[16px] md:text-[16px] text-black">
              <CardTitle product={product} onOpen={onOpen} />
            </h3>
            {categoryLabel(product) && (
              <p className=" text-text-grey reg12 lg:text-[14px] md:text-[14px]">
                {categoryLabel(product)}
              </p>
            )}
          </div>

          <div className=" divider h-px  w-full bg-blue-cs-200" />
          {hasDiscount && (
            <p className=" ml-2 text-text-danger-500 title2 line-through">
              RP{formatPrice(original)}
            </p>
          )}
          <p className=" text-black title2bold lg:text-[16px] md:text-[16px]">
            Rp {formatPrice(final)}
          </p>

          <div className="flex items-center gap-3 flex-wrap w-full">
            <button
              type="button"
              onClick={(event) => onAddCart?.(product, coverRef.current ?? event.currentTarget)}
              aria-label={`Tambah ${product.title} ke keranjang`}
              className="relative flex h-[51px] w-[51px] shrink-0 items-center justify-center rounded-[8px] border border-blue-cs-200 bg-white text-[#007AFF] transition hover:bg-[#F3F8FF]"
            >
              <ShoppingBag size={25} strokeWidth={1.8} />

              <span className="absolute -right-[7px] -top-2 flex h-5 w-5 items-center justify-center rounded-full bg-text-blue-100  text-white">
                +
              </span>
            </button>

            <button
              type="button"
              onClick={() => onBuy?.(product)}
              className="relative flex-1  body1medium btn-fill-blue"
            >
              Beli Sekarang
            </button>
          </div>
        </article>
      ) : (
        <article className="w-full py-3 flex flex-col gap-6">
          <div className="aspect-[9/12] w-full  overflow-hidden rounded-[12px]">
            {image && (
              <img src={image} alt={product.title} className="block h-full w-full object-cover" />
            )}
          </div>

          <div className="flex flex-col gap-1">
            <div className="w-full inline-flex gap-3 justify-between">
              <p className=" text-black title3bold lg:text-[16px] md:text-[16px]">{product.title}</p>

              {statusBadge}
            </div>

            {hasDiscount && (
              <p className=" text-text-danger-500 h5 line-through">RP{formatPrice(original)}</p>
            )}
            <p className=" text-black h5bold ">Rp {formatPrice(final)}</p>
          </div>
          <div className="flex flex-col w-full gap-3 flex-1">
            <div className=" flex-1 flex">
              <Button variant={'default'} onClick={onPacket} customButton="flex-1 btn-fill-blue">
                <span className="title2medium"> Lihat Paket</span>
              </Button>
            </div>
            <div className=" flex-1 flex">
              <Button
                variant={'default'}
                onClick={onCollect}
                customButton=" flex-1  title3 btn-outline-blue"
              >
                <span className="title2medium"> Lihat koleksi buku lainnya</span>
              </Button>
            </div>
          </div>
        </article>
      )}
    </>
  );
};

export default CardProductBundling;
